import clsx from 'clsx'
import renderInputField from 'components/FormControls/textInput'
import { Icon, Tooltip } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Field, reduxForm } from 'redux-form'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import * as Yup from 'yup'
import { useAccountCreateFormSubmit } from 'modules/Forms/Account/Create/useAccountCreateFormSubmit'
import baseLoginValidator from 'utils/validators/baseLoginValidator'
import LoginField from 'modules/Forms/Account/Create/subcomponents/LoginField'
import SubmitButtons from 'modules/Forms/Account/Create/subcomponents/submitButtons'
import formStyles from 'modules/Forms/common-form.module.scss'
import i18n from 'i18next'
import urlValidator from 'utils/validators/urlValidator'
import format, { formatToString } from 'utils/formatMoney'
import { onlyNumbers } from 'utils/normalize/onlyNumbers'

const BUDGET_MIN_LIMIT = 1000
const RECOMMENDED_BUDGET_MIN_LIMIT = 10000
const RECOMMENDED_BUDGET_MAX_LIMIT = 20000

export const AdGodSchema = () =>
  Yup.object().shape({
    logins: Yup.array().of(Yup.object()),
    login: Yup.string().when('logins', ([logins], schema) =>
      logins
        ? schema.strip()
        : schema
            .test('baseLoginTest', '', baseLoginValidator)
            .required('common::validation_messages.required'),
    ),
    industry: Yup.string().required('common::validation_messages.required'),
    network: Yup.string().required('common::validation_messages.required'),
    geo: Yup.string().required('common::validation_messages.required'),
    budget: Yup.string()
      .test({
        name: 'min',
        test: function (value, ctx) {
          const { path } = this

          if (+value >= BUDGET_MIN_LIMIT) {
            return true
          } else {
            return ctx.createError({
              path,
              message: i18n.t('services::forms.register_account.ad-god.fields.budget.errors.min', {
                min: formatToString(BUDGET_MIN_LIMIT, 'USD'),
              }),
            })
          }
        },
      })
      .required('common::validation_messages.required'),
    ad_object_url: Yup.string()
      .test('urlValidator', '', urlValidator)
      .required('common::validation_messages.required'),
  })

let CreateAdGodAccountForm = ({
  handleSubmit,
  submitting,
  onClose,
  formHeader,
  form,
  networkId,
  autofill,
  array,
  multiLogins,
}) => {
  const { t } = useTranslation()

  const submitAccountData = useAccountCreateFormSubmit(form, onClose, multiLogins)

  useEffect(() => {
    if (networkId) {
      autofill('network', networkId)
    }
  }, [networkId])

  return (
    <>
      {formHeader}

      <form
        onSubmit={handleSubmit(submitAccountData)}
        noValidate
      >
        <Field
          disabled
          name="network"
          hidden
          fullWidth
          component={renderInputField}
          t={t}
        />
        <div className={'margin-bottom-16'}>
          <LoginField
            form={form}
            multiLogins={multiLogins}
            array={array}
            autofill={autofill}
            networkId={networkId}
            label={'services::forms.register_account.ad-god.fields.login.label'}
          />
        </div>
        <div className={'margin-bottom-16'}>
          <Field
            name="industry"
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.ad-god.fields.industry.label')}
            t={t}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="geo"
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.ad-god.fields.geo.label')}
            t={t}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t('services::forms.register_account.ad-god.fields.geo.tooltip')}
              placement="top"
              arrow
            >
              <Icon
                name={'information'}
                color={'secondary-grey'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="budget"
            type="text"
            fullWidth
            normalize={onlyNumbers}
            component={renderInputField}
            label={t('services::forms.register_account.ad-god.fields.budget.label')}
            t={t}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t('services::forms.register_account.ad-god.fields.budget.tooltip', {
                min: format(RECOMMENDED_BUDGET_MIN_LIMIT, 'USD'),
                max: format(RECOMMENDED_BUDGET_MAX_LIMIT, 'USD'),
              })}
              placement="top"
              arrow
            >
              <Icon
                name={'information'}
                color={'secondary-grey'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>

        <div className={'margin-bottom-16'}>
          <Field
            name="ad_object_url"
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.ad-god.fields.ad_object_url.label')}
            t={t}
          />
        </div>

        <SubmitButtons
          submitting={submitting}
          onClose={onClose}
        />
      </form>
    </>
  )
}

CreateAdGodAccountForm = reduxForm({
  form: 'CreateAdGodAccountForm',
  initialValues: {
    login: '',
    industry: '',
    geo: '',
    budget: '',
    ad_object_url: '',
  },
  shouldAsyncValidate: shouldAsyncValidate,
})(CreateAdGodAccountForm)

CreateAdGodAccountForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  autofill: PropTypes.func,
  networkId: PropTypes.string,
  networkType: PropTypes.string,
  initialValues: PropTypes.object,
  form: PropTypes.string,
  multiLogins: PropTypes.bool,
  array: PropTypes.object,
  formHeader: PropTypes.node,
}

export default CreateAdGodAccountForm
